import { API } from './config'
import { getField, parseS3Documents } from '../Helpers/Misc'
import { parseSchoolData } from '../Helpers/API'
import { MASTERCLASS } from '../Constants/broadcasts'

export const getAdminTeacherList = () => {
  return API.get('/admin/teacher-info/become-teacher-requests')
    .then((res) => {
      return res.data.data
    })
    .catch(() => false)
}

export const processTeacherRequest = ({ status, userId, note }) =>
  API.post(`/admin/teacher-info/process`, {
    status,
    userId,
    note,
  })
    .then(() => true)
    .catch(() => false)

export const uploadTeacherCover = (data, teacherID, onProgress) =>
  API.post(`/teacher/upload-cover/${teacherID}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      let percentCompleted = progressEvent.loaded / progressEvent.total

      onProgress(Math.min(percentCompleted * 100, 99))
    },
  })
    .then((res) => getField(res, 'data.data.location'))
    .catch(() => false)

export const getAdminCourseList = () =>
  API.get('/admin/courses')
    .then((res) =>
      res.data.data.courses
        .filter((course) => course.status !== 'DELETED')
        .map((course) => ({
          ...course,
          coverOptions: [
            ...parseS3Documents(
              getField(course, 'teacher.teacherInfo.coverImagesEdited') || [],
            ),
            ...parseS3Documents(
              getField(course, 'teacher.teacherInfo.coverImagesRaw') || [],
            ),
          ],
          status:
            course.tainted && course.status === 'LIVE'
              ? 'PENDING_EDITS'
              : course.status,
        })),
    )
    .catch(() => false)

export const processCourseApprovalRequest = ({ status, id, note }) =>
  API.post(`/admin/process-course/${id}`, {
    status,
    note,
  })
    .then(() => true)
    .catch(() => false)

export const processCourseEditRequest = ({ status, id }) =>
  API.post(`/admin/approve-reject-course/${id}/${status}`)
    .then(() => true)
    .catch(() => false)

export const setTeacherCover = (teacherId, imageId) =>
  API.post('/teacher/make-cover-image', {
    teacherId,
    imageId,
  })
    .then((res) => res.data.data)
    .catch(() => false)

export const setCourseCover = (teacherId, imageId, courseId) =>
  API.post('/courses/add-course-cover/', {
    teacherId,
    imageId,
    courseId,
  })
    .then((res) => ({
      fileName: getField(res.data.data, 'fileName'),
      fileType: getField(res.data.data, 'metaData.s3.type'),
      fileSize: getField(res.data.data, 'metaData.s3.size'),
      location: getField(res.data.data, 'metaData.s3.extra.Location'),
    }))
    .catch(() => false)

export const deleteCourse = (courseID) =>
  API.delete(`/courses/${courseID}`)
    .then(() => true)
    .catch(() => false)

export const deleteTeacher = (teacherID) =>
  API.delete(`/teacher/${teacherID}`)
    .then(() => true)
    .catch(() => false)

export const togglePrivate = ({ teacherId, status }) =>
  API.post(`/admin/alter-teacher-state`, {
    teacherId,
    status,
  })
    .then(() => true)
    .catch(() => false)

export const setTeacherCoverVideo = (externalProfileVideo, teacherId) =>
  API.put(`/teacher/profile-video/upload`, {
    teacherId,
    externalProfileVideo,
  })
    .then(() => true)
    .catch(() => false)

export const toggleMaster = ({ teacherId, status }) =>
  API.post(`/admin/alter-teacher-master-state`, {
    teacherId,
    status,
  })
    .then(() => true)
    .catch(() => false)

export const toggleDemo = ({ teacherId, status }) =>
  API.post(`/admin/alter-teacher-demo-state`, {
    teacherId,
    status,
  })
    .then(() => true)
    .catch(() => false)

export const getAllSchools = () =>
  API.get(`/admin/schools`)
    .then((res) => ({
      schools: res.data.data.schools.map((data) => parseSchoolData(data)),
      teachers: res.data.data.teachers,
    }))
    .catch(() => false)

export const createSchool = ({ name, location, schoolHandle }) =>
  API.post(`/admin/school`, {
    name,
    location,
    schoolHandle,
  })
    .then(({ data: { data } }) => parseSchoolData(data))
    .catch(() => false)

export const addTeacherToSchool = (schoolId, teacherId) =>
  API.post(`/admin/school/addTeacher`, {
    schoolId,
    teacherId,
  })
    .then(({ data: { data } }) => parseSchoolData(data))
    .catch(() => false)

export const makeSchoolAdmin = (schoolId, teacherId, status) =>
  API.put(`/admin/school/makeAdmin`, {
    schoolId,
    teacherId,
    status,
  })
    .then(({ data: { data } }) => parseSchoolData(data))
    .catch(() => false)

export const deleteTeacherFromSchool = (teacherId, schoolId) =>
  API.delete(`admin/school/deleteTeacher`, {
    data: {
      schoolId,
      teacherId,
    },
  })
    .then(({ data: { data } }) => parseSchoolData(data))
    .catch(() => false)

export const updateCourseDetailsAdmin = (updates, courseID, teacherID) =>
  API.put(`admin/courses/${courseID}`, { ...updates, teacherId: teacherID })
    .then((res) => res.data.data)
    .catch(() => false)

export const getAdminReviews = async () => [
  ...(await API.get(`/admin/course/reviews`)
    .then((res) =>
      res.data.data.map((review) => ({ ...review, type: 'course' })),
    )
    .catch(() => false)),
  ...(await API.get(`/admin/teacher/reviews`)
    .then((res) =>
      res.data.data.map((review) => ({ ...review, type: 'teacher' })),
    )
    .catch(() => false)),
]

export const approveReview = ({ type, ...data }) =>
  API.put(`/admin/reviews/${type}`, data)
    .then(() => true)
    .catch(() => false)

export const uploadTnt = (data) =>
  API.post('/tnt', data)
    .then((res) => res.data.data)
    .catch(() => false)

export const getTnt = () =>
  API.get('/tnt')
    .then(({ data: { data: { student, teacher } } }) => [
      ...Object.keys(student).map((key) => ({
        id: key,
        type: 'student',
        ...student[key],
      })),
      ...Object.keys(teacher).map((key) => ({
        id: key,
        type: 'teacher',
        ...teacher[key],
      })),
    ])
    .catch(() => false)

export const deleteTnt = (tntId, type) =>
  API.delete(`/tnt`, { data: { tntId, type } })
    .then(() => true)
    .catch(() => false)

export const getBroadcasts = (broadcastType = MASTERCLASS) =>
  API.get(`/admin/broadcast-classes/${broadcastType}`)
    .then((res) =>
      res.data.data
        .sort((a, b) => Number(a.startTime) - Number(b.startTime))
        .map((bc) => {
          return {
            ...bc,
            countryDiscounts: Object.entries(bc?.coupon?.value || {}).map(
              ([currency, { percentage }]) => ({
                currency,
                discount: percentage,
              }),
            ),
          }
        }),
    )
    .catch(() => false)

export const getBroadcastSeries = () =>
  API.get(`/series/`)
    .then((res) => res.data.data)
    .catch(() => false)

export const addSnippetToMasterclass = (
  broadcastClassId,
  description,
  profileVideo,
  showReelVideos,
  classSnippets,
  broadcastType = MASTERCLASS,
) =>
  API.put(`/admin/broadcast-class/${broadcastType}`, {
    broadcastClassId,
    description,
    profileVideo,
    showReelVideos,
    classSnippets,
  })
    .then(() => true)
    .catch(() => false)

export const addEmailToMasterclassWhitelist = (
  broadcastClassId,
  emails,
  broadcastType = MASTERCLASS,
) =>
  API.put(`/admin/broadcast-class/${broadcastType}/whitelist`, {
    broadcastClassId,
    emails,
  })
    .then(() => true)
    .catch(() => false)

export const approveMasterclass = (
  broadcastClassId,
  broadcastType = MASTERCLASS,
) =>
  API.put(`/broadcast-class/${broadcastType}/admin/approve`, {
    broadcastClassId,
  })
    .then(() => true)
    .catch(() => false)

export const editMasterclassesAdmin = (id, data, broadcastType = MASTERCLASS) =>
  API.put(`/admin/broadcast-class/${broadcastType}`, {
    ...data,
    broadcastClassId: id,
  })
    .then(() => true)
    .catch(() => false)

export const updateMasterclassCountryDiscount = (
  id,
  data,
  broadcastType = MASTERCLASS,
) =>
  API.post(`/broadcast-class/${broadcastType}/${id}/coupon`, data)
    .then(() => true)
    .catch(() => false)

export const submitMasterclassRecordingId = (
  id,
  data,
  broadcastType = MASTERCLASS,
) =>
  API.put(`/admin/broadcast-class/${broadcastType}`, {
    broadcastClassId: data.id,
    recordingId: id,
  })
    .then(() => true)
    .catch(() => false)

export const deleteBroadcastClass = (id, broadcastType = MASTERCLASS) =>
  API.delete(`/broadcast-class/${broadcastType}/${id}`)
    .then(() => true)
    .catch(() => false)

export const sendBroadcastJoinInstructions = (
  broadcastClassId,
  broadcastType = MASTERCLASS,
) =>
  API.post(
    `/admin/broadcast-class/${broadcastType}/${broadcastClassId}/send-instructions`,
  )
    .then(() => true)
    .catch(() => false)

export const sendBroadcastReminderEmails = (
  broadcastClassId,
  broadcastType = MASTERCLASS,
) =>
  API.post(
    `/admin/broadcast-class/${broadcastType}/${broadcastClassId}/send-reminder`,
  )
    .then(() => true)
    .catch(() => false)

export const sendBroadcastRecordingEmails = (
  broadcastClassId,
  broadcastType = MASTERCLASS,
) =>
  API.post(
    `/admin/broadcast-class/${broadcastType}/${broadcastClassId}/send-recording`,
  )
    .then(() => true)
    .catch(() => false)

export const markBillPaid = (billId) =>
  API.post(`admin/broadcast-class/make-bill-paid`, { billId })
    .then(() => true)
    .catch(() => false)

export const sendMasterclassEmail = (
  body,
  paid,
  unPaid,
  id,
  broadcastType = MASTERCLASS,
) =>
  API.post(
    `/admin/broadcast-class/${broadcastType}/${id}/send-generic-instructions`,
    { body, paid, unPaid },
  )
    .then(() => true)
    .catch(() => false)

export const uploadMasterclassCover = (
  masterclassId,
  data,
  broadcastType = MASTERCLASS,
) =>
  API.post(
    `/admin/broadcast-class/${broadcastType}/${masterclassId}/upload-cover-image`,
    data,
  )
    .then(() => true)
    .catch(() => false)

export const sendTeacherFollowerEmails = (data) =>
  API.post(`/admin/send-follower-email`, data)
    .then(() => true)
    .catch(() => false)

export const getPaymentInfo = (id) =>
  API.get(`/admin/payments/${id}`)
    .then((res) => res.data.data)
    .catch(() => false)

export const getPaymentInfoCSV = (id) =>
  API.get(`/admin/payments/${id}/csv`)
    .then((res) => res.data)
    .catch(() => false)

export const approveSeriesRequest = (id, status, type = MASTERCLASS) =>
  API.put(`/admin/series/approve-reject/${id}/${type}`, { status })
    .then(() => true)
    .catch(() => false)

export const toggleSeriesPrivateState = (id, status, type = MASTERCLASS) =>
  API.put(`/series/${type}/${id}/alter-series-state`, {
    isPrivate: status,
  })
    .then(() => true)
    .catch(() => false)

export const makeBroadcastClassModerator = ({
  broadcastClassId,
  emails,
  type,
  state,
}) =>
  API.put(`/admin/broadcast-class/${type}/toggle-moderator-state`, {
    broadcastClassId,
    emails,
    state,
  })
    .then(() => true)
    .catch(() => false)

export const markSeriesBillPaidRequest = (billId) =>
  API.post(`/admin/series/make-bill-paid`, {
    billId,
  })
    .then(() => true)
    .catch(() => false)

export const setArtistTypeRequest = (id, type) =>
  API.put(`/admin/teacher/${id}/add-artist-type`, { artistType: type })
    .then(() => true)
    .catch(() => false)

export const setSessionTypeRequest = (id, types) =>
  API.put(`/admin/teacher/${id}/add-session-types`, { sessionTypes: types })
    .then(() => true)
    .catch(() => false)
